import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, Check } from "lucide-react";
import useReveal from "../hooks/useReveal";
import { toast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import { createLead } from "../lib/api";

const SUBJECTS = [
  "Achat d'une propriété",
  "Financement",
  "Formation",
  "Événement / visite VIP",
  "Autre",
];

export default function Contact() {
  useReveal([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: SUBJECTS[0], message: "" });
  const [website, setWebsite] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Champs manquants", description: "Merci de remplir tous les champs obligatoires." });
      return;
    }
    try {
      setSubmitting(true);
      await createLead({ ...form, website });
      setSent(true);
      toast({ title: "Message envoyé", description: "Nous vous recontacterons très vite." });
      setForm({ name: "", email: "", phone: "", subject: SUBJECTS[0], message: "" });
    } catch (err) {
      const msg = err?.response?.status === 429 ? "Trop de messages envoyés. Réessayez plus tard." : "L'envoi a échoué, veuillez réessayer.";
      toast({ title: "Erreur", description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Toaster />
      <section className="relative pt-40 pb-20 bg-[#0b1f33] overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1541417904950-b855846fe074)" }} />
        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <p className="text-[#2f74c9] tracking-[0.3em] text-xs uppercase mb-4">Contact</p>
          <h1 className="font-serif-display text-4xl md:text-6xl text-white">Parlons de votre projet</h1>
          <p className="text-white/70 mt-5 max-w-2xl mx-auto">
            Un conseiller vous r&eacute;pond sous 24h. Appel gratuit et sans engagement.
          </p>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* info */}
          <div className="reveal space-y-6">
            {[
              { Icon: MapPin, label: "Adresse", value: "Playa del Carmen, Quintana Roo, MX" },
              { Icon: Phone, label: "Téléphone", value: "+52 984 000 0000" },
              { Icon: Mail, label: "Courriel", value: "info@bellaedifices.com" },
            ].map(({ Icon, label, value }) => (
              <div key={label} className="flex items-start gap-4 bg-white rounded-lg p-6 border border-slate-100">
                <div className="w-11 h-11 rounded-full bg-[#0b1f33] flex items-center justify-center shrink-0">
                  <Icon size={20} className="text-[#2f74c9]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400">{label}</p>
                  <p className="text-[#0b1f33] font-500 mt-1">{value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* form */}
          <div className="reveal reveal-delay-1 lg:col-span-2 bg-white rounded-lg p-8 md:p-10 border border-slate-100 shadow-sm">
            {sent ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                  <Check size={32} className="text-emerald-600" />
                </div>
                <h3 className="font-serif-display text-3xl text-[#0b1f33] mt-6">Merci&nbsp;!</h3>
                <p className="text-slate-500 mt-2">Votre message a bien &eacute;t&eacute; envoy&eacute;. Nous vous recontacterons rapidement.</p>
                <button onClick={() => setSent(false)} className="btn-gold mt-8 px-6 py-3 rounded-sm font-600">
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <input type="text" name="website" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="contact-honeypot" style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm text-slate-600 mb-1.5">Nom complet *</label>
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-md border border-slate-200 px-4 py-3 outline-none focus:border-[#2f74c9] focus:ring-2 focus:ring-[#2f74c9]/20 transition" placeholder="Votre nom" />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-1.5">Courriel *</label>
                    <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-md border border-slate-200 px-4 py-3 outline-none focus:border-[#2f74c9] focus:ring-2 focus:ring-[#2f74c9]/20 transition" placeholder="vous@email.com" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm text-slate-600 mb-1.5">Téléphone</label>
                    <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded-md border border-slate-200 px-4 py-3 outline-none focus:border-[#2f74c9] focus:ring-2 focus:ring-[#2f74c9]/20 transition" placeholder="+1 000 000 0000" />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-1.5">Sujet</label>
                    <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full rounded-md border border-slate-200 px-4 py-3 outline-none focus:border-[#2f74c9] focus:ring-2 focus:ring-[#2f74c9]/20 transition bg-white">
                      {SUBJECTS.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-slate-600 mb-1.5">Message *</label>
                  <textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-md border border-slate-200 px-4 py-3 outline-none focus:border-[#2f74c9] focus:ring-2 focus:ring-[#2f74c9]/20 transition resize-none" placeholder="Décrivez votre projet..." />
                </div>
                <button type="submit" disabled={submitting} className="btn-gold w-full md:w-auto px-8 py-3.5 rounded-sm font-600 flex items-center justify-center gap-2 disabled:opacity-60">
                  {submitting ? "Envoi…" : "Envoyer le message"} <Send size={16} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
